import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { LanguageService } from 'app/core/i18n/language.service';
import { environment } from 'environments/environment';
import { Observable, catchError, throwError } from 'rxjs';

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);

    // Language: only our own API gets Accept-Language, never ./i18n/*, the
    // in-browser mock API or a third-party host (§4.4).
    const apiBase = environment.serverUrl + environment.apiRoute;
    let newReq = req.url.startsWith(apiBase)
        ? req.clone({
              setHeaders: { 'Accept-Language': inject(LanguageService).current },
          })
        : req.clone();

    // Request
    //
    // If the access token didn't expire, add the Authorization header.
    // We won't add the Authorization header if the access token expired.
    // This will force the server to return a "401 Unauthorized" response
    // for the protected API routes which our response interceptor will
    // catch and delete the access token from the local storage while logging
    // the user out from the app.
    if (
        authService.accessToken &&
        !AuthUtils.isTokenExpired(authService.accessToken)
    ) {
        // Build on newReq (not req) so Accept-Language is kept.
        let headers = newReq.headers.set(
            'Authorization',
            'Bearer ' + authService.accessToken
        );

        const activeLocationId = localStorage.getItem('activeLocationId');
        if (activeLocationId) {
            headers = headers.set('Location-Id', activeLocationId);
        }

        newReq = newReq.clone({
            headers: headers,
        });
    }

    // Response
    return next(newReq).pipe(
        catchError((error) => {
            // Catch "401 Unauthorized" responses
            if (error instanceof HttpErrorResponse && error.status === 401) {
                // Sign out
                authService.signOut();

                // Reload the app
                location.reload();
            }

            return throwError(error);
        })
    );
};
